import {
  collection,
  addDoc,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  query,
  where,
  orderBy,
  QueryConstraint,
  writeBatch,
} from 'firebase/firestore';
import { db } from './firebase';
import type {
  SurveyResponse,
  CustomerDetails,
  SatisfactionRatings,
  OpenEnded,
  Consent,
  FilterState,
} from '@/types';

const SURVEY_RESPONSES_COLLECTION = 'surveyResponses';

// Create a survey response (public, no auth required)
export const createSurveyResponse = async (
  details: CustomerDetails,
  ratings: SatisfactionRatings,
  open: OpenEnded,
  consent: Consent
): Promise<string> => {
  if (!consent.agreed) {
    throw new Error('Consent must be agreed to submit survey');
  }

  const now = new Date().toISOString();
  const responseData: Omit<SurveyResponse, 'id'> = {
    createdAt: now,
    details,
    ratings,
    open,
    consent: {
      ...consent,
      timestamp: now,
    },
    meta: {
      source: 'link', // Default, can be enhanced with UTM tracking
      deleted: false,
    },
  };

  const docRef = await addDoc(
    collection(db, SURVEY_RESPONSES_COLLECTION),
    responseData
  );

  return docRef.id;
};

// Get survey response by ID (authenticated only)
export const getSurveyResponse = async (
  id: string
): Promise<SurveyResponse | null> => {
  const docRef = doc(db, SURVEY_RESPONSES_COLLECTION, id);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  const data = docSnap.data();
  return {
    id: docSnap.id,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
    details: data.details,
    ratings: data.ratings,
    open: data.open,
    consent: data.consent,
    meta: data.meta,
  } as SurveyResponse;
};

// Get all survey responses with filters (authenticated only)
export const getSurveyResponses = async (
  filters?: FilterState
): Promise<SurveyResponse[]> => {
  const constraints: QueryConstraint[] = [];

  if (filters?.dateRange) {
    constraints.push(
      where('createdAt', '>=', filters.dateRange.start),
      where('createdAt', '<=', filters.dateRange.end)
    );
  }

  if (filters?.productOrService) {
    constraints.push(
      where('details.productOrService', '==', filters.productOrService)
    );
  }

  if (filters?.companyName) {
    constraints.push(
      where('details.companyName', '==', filters.companyName)
    );
  }

  if (filters?.handledByStaff) {
    constraints.push(
      where('meta.handledByStaff', '==', filters.handledByStaff)
    );
  }

  constraints.push(orderBy('createdAt', 'desc'));

  const q = query(
    collection(db, SURVEY_RESPONSES_COLLECTION),
    ...constraints
  );

  const querySnapshot = await getDocs(q);
  const results = querySnapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    ...docSnap.data(),
  })) as SurveyResponse[];

  // If not showing deleted, filter out items where meta.deleted is true (or missing)
  if (!filters?.showDeleted) {
    return results.filter((r) => r.meta?.deleted !== true || r.meta?.deleted === undefined);
  }

  return results;
};

// Update survey response (admin only)
export const updateSurveyResponse = async (
  id: string,
  updates: Partial<SurveyResponse>
): Promise<void> => {
  const docRef = doc(db, SURVEY_RESPONSES_COLLECTION, id);
  const updateData: Record<string, unknown> = {
    updatedAt: new Date().toISOString(),
  };

  if (updates.meta) {
    // If updating meta, merge with existing if needed
    const existing = await getDoc(docRef);
    if (existing.exists()) {
      const existingMeta = existing.data().meta;
      updateData.meta = { ...existingMeta, ...updates.meta };
    } else {
      updateData.meta = updates.meta;
    }
  }

  if (updates.details) {
    updateData.details = updates.details;
  }

  if (updates.ratings) {
    updateData.ratings = updates.ratings;
  }

  if (updates.open) {
    updateData.open = updates.open;
  }

  await updateDoc(docRef, updateData);
};

// Soft delete survey response (admin only)
export const deleteSurveyResponse = async (id: string): Promise<void> => {
  const docRef = doc(db, SURVEY_RESPONSES_COLLECTION, id);
  await updateDoc(docRef, {
    'meta.deleted': true,
    updatedAt: new Date().toISOString(),
  });
};

// Restore survey response (admin only)
export const restoreSurveyResponse = async (id: string): Promise<void> => {
  const docRef = doc(db, SURVEY_RESPONSES_COLLECTION, id);
  await updateDoc(docRef, {
    'meta.deleted': false,
    updatedAt: new Date().toISOString(),
  });
};

// Bulk import survey responses (admin only)
export const bulkImportSurveyResponses = async (
  responses: Omit<SurveyResponse, 'id' | 'createdAt'>[]
): Promise<number> => {
  const batch = writeBatch(db);
  const now = new Date().toISOString();

  responses.forEach((response) => {
    const docRef = doc(collection(db, SURVEY_RESPONSES_COLLECTION));
    batch.set(docRef, {
      ...response,
      createdAt: now,
    });
  });

  await batch.commit();
  return responses.length;
};
